import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  PbxApiKey,
  PbxApiKeyKind,
  PbxApiKeyProvider,
} from '@xsc/shared';
import { api } from '../lib/api-client.js';

/**
 * Admin → PBX API Keys.
 *
 * Stores vendor PBX credentials (Wildix S2S, future 3CX, etc.) in the
 * server-side encrypted vault. Secrets never travel back to this page after
 * creation — only the masked fingerprint shows up in the list.
 *
 * Wildix S2S reference: https://wildix.atlassian.net/wiki/x/dAANWw
 */

interface ListResp { data: PbxApiKey[] }

export function AdminPbxApiKeysPage(): JSX.Element {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['pbx-api-keys'],
    queryFn: async (): Promise<ListResp> => {
      return (await api.get('/v1/admin/pbx-api-keys')) as ListResp;
    },
  });

  const del = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/v1/admin/pbx-api-keys/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pbx-api-keys'] }),
  });

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">PBX API Keys</h1>
          <p className="text-sm text-ink-muted">
            Credentials used by x-streamConnect to call vendor PBX APIs (Wildix S2S /
            OAuth, future 3CX, etc.).{' '}
            <a
              className="underline"
              href="https://wildix.atlassian.net/wiki/x/dAANWw"
              target="_blank"
              rel="noreferrer"
            >
              Wildix S2S docs ↗
            </a>
          </p>
        </div>
      </header>

      <ConnectWildixCloudCard />

      <CreateKeyForm onCreated={() => qc.invalidateQueries({ queryKey: ['pbx-api-keys'] })} />

      <section>
        <h2 className="text-lg font-medium mb-2">Existing keys</h2>
        {list.isLoading && <p className="text-sm text-ink-muted">Loading…</p>}
        {list.isError && (
          <p className="text-sm text-red-500">Failed to load keys.</p>
        )}
        {list.data && list.data.data.length === 0 && (
          <p className="text-sm text-ink-muted">No keys yet.</p>
        )}
        {list.data && list.data.data.length > 0 && (
          <div className="border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left">
                <tr>
                  <th className="px-3 py-2">Label</th>
                  <th className="px-3 py-2">Provider</th>
                  <th className="px-3 py-2">Kind</th>
                  <th className="px-3 py-2">Secret</th>
                  <th className="px-3 py-2">Created</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {list.data.data.map((k) => (
                  <tr key={k.id} className="border-t border-border">
                    <td className="px-3 py-2">{k.label}</td>
                    <td className="px-3 py-2">{k.provider}</td>
                    <td className="px-3 py-2">{k.kind}</td>
                    <td className="px-3 py-2"><code>{k.fingerprint}</code></td>
                    <td className="px-3 py-2 text-ink-muted">
                      {new Date(k.created_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        className="text-xs text-red-500 hover:underline"
                        onClick={() => {
                          if (confirm(`Delete "${k.label}"? This can't be undone.`)) {
                            del.mutate(k.id);
                          }
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * "Connect Wildix Cloud" card — runs the OAuth 2.0 authorization_code
 * dance. Shows the exact redirect URI to paste into Wildix's OAuth app
 * form, plus a copy-to-clipboard button so the admin never has to leave
 * this page to set things up.
 */
function ConnectWildixCloudCard(): JSX.Element {
  const [pbxBaseUrl, setPbxBaseUrl] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Fetch the canonical redirect URI from the server so it always matches
  // whatever ISSUER_URL is configured (prod vs preview).
  const redirect = useQuery({
    queryKey: ['wildix-redirect-uri'],
    queryFn: async (): Promise<{ redirect_uri: string }> =>
      (await api.get('/v1/oauth/wildix/redirect-uri')) as { redirect_uri: string },
  });
  const redirectUri =
    redirect.data?.redirect_uri ?? 'https://app.x-streamconnect.com/v1/oauth/wildix/callback';

  // Surface ?wildix_connected=1 / ?wildix_error=… from the callback redirect.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('wildix_connected') === '1') {
      setBanner('Wildix Cloud connected successfully.');
      const url = new URL(window.location.href);
      url.searchParams.delete('wildix_connected');
      window.history.replaceState({}, '', url.toString());
    } else if (params.get('wildix_error')) {
      setErr(`Wildix returned an error: ${params.get('wildix_error')}`);
      const url = new URL(window.location.href);
      url.searchParams.delete('wildix_error');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  const connect = useMutation({
    mutationFn: async () => {
      setErr(null);
      const resp = (await api.post('/v1/oauth/wildix/start', {
        pbx_base_url: pbxBaseUrl,
        client_id: clientId,
        client_secret: clientSecret,
      })) as { authorize_url: string };
      // Hand off to Wildix — full top-level navigation, not a popup, so
      // the eventual callback lands back here as a normal page load.
      window.location.href = resp.authorize_url;
    },
    onError: (e) => setErr(e instanceof Error ? e.message : String(e)),
  });

  const copyRedirect = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(redirectUri);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setErr('Could not copy to clipboard');
    }
  };

  return (
    <section className="border border-border rounded-md p-4 bg-card space-y-4">
      <div>
        <h2 className="text-lg font-medium">Connect Wildix Cloud (OAuth 2.0)</h2>
        <p className="text-sm text-ink-muted">
          Register an OAuth 2.0 application in your Wildix PBX admin console,
          then paste the resulting Client ID and Client secret below. We&rsquo;ll
          handle the consent redirect, store the tokens encrypted at rest, and
          refresh them automatically.
        </p>
      </div>

      {banner && (
        <div className="rounded border border-green-600/50 bg-green-600/10 px-3 py-2 text-sm text-green-300">
          {banner}
        </div>
      )}

      <div className="rounded border border-border bg-bg/40 p-3 space-y-3">
        <div>
          <h3 className="text-sm font-medium">Wildix PBX → OAuth 2.0 application form</h3>
          <p className="text-xs text-ink-muted">
            Open <em>PBX → Integrations → API → OAuth 2.0</em> and create a new
            application with exactly these values:
          </p>
        </div>
        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
          <dt className="text-ink-muted">Application name</dt>
          <dd>
            <code className="px-1.5 py-0.5 bg-bg rounded border border-border">
              x-streamConnect
            </code>
          </dd>
          <dt className="text-ink-muted">Redirect URI</dt>
          <dd className="flex items-stretch gap-2">
            <input
              readOnly
              value={redirectUri}
              onFocus={(e) => e.currentTarget.select()}
              className="flex-1 px-2 py-1 bg-bg border border-border rounded font-mono text-xs"
            />
            <button
              type="button"
              onClick={copyRedirect}
              className="px-2 py-1 bg-accent text-accent-foreground rounded text-xs whitespace-nowrap"
              aria-label="Copy redirect URI"
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </dd>
          <dt className="text-ink-muted">Expire date</dt>
          <dd>
            <code className="px-1.5 py-0.5 bg-bg rounded border border-border">365 days</code>
          </dd>
        </dl>
        <p className="text-xs text-ink-muted">
          After saving, Wildix will show you a Client ID and Client secret —
          paste them below.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <label className="text-sm sm:col-span-3">
          <span className="block mb-1">PBX base URL</span>
          <input
            value={pbxBaseUrl}
            onChange={(e) => setPbxBaseUrl(e.target.value)}
            placeholder="https://your-tenant.wildixin.com"
            className="w-full px-2 py-1 bg-bg border border-border rounded"
          />
        </label>
        <label className="text-sm sm:col-span-1">
          <span className="block mb-1">Client ID</span>
          <input
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            className="w-full px-2 py-1 bg-bg border border-border rounded"
          />
        </label>
        <label className="text-sm sm:col-span-2">
          <span className="block mb-1">Client secret</span>
          <input
            type="password"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            className="w-full px-2 py-1 bg-bg border border-border rounded"
          />
        </label>
      </div>

      {err && <p className="text-sm text-red-500">{err}</p>}

      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={connect.isPending || !pbxBaseUrl || !clientId || !clientSecret}
          onClick={() => connect.mutate()}
          className="px-3 py-1.5 bg-accent text-accent-foreground rounded-md text-sm disabled:opacity-50"
        >
          {connect.isPending ? 'Redirecting to Wildix…' : 'Connect Wildix Cloud'}
        </button>
        <span className="text-xs text-ink-muted">
          You&rsquo;ll be redirected to your PBX to grant access, then sent back here.
        </span>
      </div>
    </section>
  );
}

function CreateKeyForm({ onCreated }: { onCreated: () => void }): JSX.Element {
  const [provider, setProvider] = useState<PbxApiKeyProvider>('wildix');
  const [kind, setKind] = useState<PbxApiKeyKind>('s2s');
  const [label, setLabel] = useState('');
  const [user, setUser] = useState('');
  const [pass, setPass] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [tokenUrl, setTokenUrl] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      setErr(null);
      let secrets;
      if (kind === 's2s') secrets = { kind, user, pass };
      else if (kind === 'oauth2')
        secrets = { kind, client_id: clientId, client_secret: clientSecret, ...(tokenUrl ? { token_url: tokenUrl } : {}) };
      else secrets = { kind, api_key: apiKey };

      await api.post('/v1/admin/pbx-api-keys', { provider, label, scopes: [], secrets });
    },
    onSuccess: () => {
      setLabel(''); setUser(''); setPass('');
      setClientId(''); setClientSecret(''); setApiKey(''); setTokenUrl('');
      onCreated();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : String(e)),
  });

  return (
    <section className="border border-border rounded-md p-4 bg-card space-y-3">
      <h2 className="text-lg font-medium">Add a key</h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <label className="text-sm">
          <span className="block mb-1">Provider</span>
          <select
            value={provider}
            onChange={(e) => setProvider(e.target.value as PbxApiKeyProvider)}
            className="w-full px-2 py-1 bg-bg border border-border rounded"
          >
            <option value="wildix">Wildix</option>
            <option value="3cx">3CX</option>
            <option value="netsapiens">NetSapiens</option>
            <option value="other">Other</option>
          </select>
        </label>
        <label className="text-sm">
          <span className="block mb-1">Kind</span>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as PbxApiKeyKind)}
            className="w-full px-2 py-1 bg-bg border border-border rounded"
          >
            <option value="s2s">Server-to-server (user + pass)</option>
            <option value="oauth2">OAuth2 (client id + secret)</option>
            <option value="api_key">API key (bearer)</option>
          </select>
        </label>
        <label className="text-sm">
          <span className="block mb-1">Label</span>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Wildix prod S2S"
            className="w-full px-2 py-1 bg-bg border border-border rounded"
          />
        </label>
      </div>

      {kind === 's2s' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-sm">
            <span className="block mb-1">User</span>
            <input
              value={user}
              onChange={(e) => setUser(e.target.value)}
              className="w-full px-2 py-1 bg-bg border border-border rounded"
            />
          </label>
          <label className="text-sm">
            <span className="block mb-1">Password</span>
            <input
              type="password"
              value={pass}
              onChange={(e) => setPass(e.target.value)}
              className="w-full px-2 py-1 bg-bg border border-border rounded"
            />
          </label>
        </div>
      )}

      {kind === 'oauth2' && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="text-sm">
            <span className="block mb-1">Client ID</span>
            <input
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className="w-full px-2 py-1 bg-bg border border-border rounded"
            />
          </label>
          <label className="text-sm">
            <span className="block mb-1">Client secret</span>
            <input
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              className="w-full px-2 py-1 bg-bg border border-border rounded"
            />
          </label>
          <label className="text-sm">
            <span className="block mb-1">Token URL (optional)</span>
            <input
              value={tokenUrl}
              onChange={(e) => setTokenUrl(e.target.value)}
              placeholder="https://…/oauth/token"
              className="w-full px-2 py-1 bg-bg border border-border rounded"
            />
          </label>
        </div>
      )}

      {kind === 'api_key' && (
        <label className="text-sm block">
          <span className="block mb-1">API key</span>
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            className="w-full px-2 py-1 bg-bg border border-border rounded"
          />
        </label>
      )}

      {err && <p className="text-sm text-red-500">{err}</p>}
      <div>
        <button
          type="button"
          disabled={create.isPending || !label}
          onClick={() => create.mutate()}
          className="px-3 py-1.5 bg-accent text-accent-foreground rounded-md text-sm disabled:opacity-50"
        >
          {create.isPending ? 'Saving…' : 'Save key'}
        </button>
      </div>
    </section>
  );
}
